import { useState, useRef } from 'react'
import './ArticleEditor.css'

const uid = () => Math.random().toString(36).slice(2, 9) + Date.now().toString(36)
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v))

// ─── Layer factories ───────────────────────────────────────────────────────────

const mkPage = () => ({
    id: uid(),
    background: { type: 'color', color: '#1a1a2e' },
    layers: [],
})

const mkTextLayer = () => ({
    id: uid(),
    type: 'text',
    x: 50, y: 50,
    rotation: 0, scale: 1, opacity: 1,
    data: {
        content: 'Votre texte',
        fontSize: 6, fontWeight: '700',
        color: '#ffffff', align: 'center',
        bgColor: null, bgPadding: 8, bgRadius: 6,
        shadow: true, maxWidthPct: 90,
    },
})

const mkStickerLayer = (emoji) => ({
    id: uid(),
    type: 'sticker',
    x: 50, y: 50,
    rotation: 0, scale: 1, opacity: 1,
    data: { emoji, url: null, alt: '' },
})

const mkMentionLayer = (displayName, entityType) => ({
    id: uid(),
    type: 'mention',
    x: 50, y: 50,
    rotation: 0, scale: 1, opacity: 1,
    data: { entityType, entityId: '', displayName },
})

const mkProductTagLayer = (product) => ({
    id: uid(),
    type: 'product-tag',
    x: 50, y: 50,
    rotation: 0, scale: 1, opacity: 1,
    data: {
        productId: String(product.id),
        name: product.name,
        price: product.price != null ? String(product.price) : undefined,
        currency: product.currency,
        image: product.image || null,
    },
})

// ─── Sticker library ──────────────────────────────────────────────────────────

const STICKER_EMOJIS = [
    '❤️','🔥','⭐','🎉','✨','😍','🥳','😂','💯','👏',
    '🙌','💪','🎊','🌟','💥','🤩','😎','🥰','💖','🎶',
    '🏆','💎','🚀','🌈','🦋','🌸','🍀','⚡','🎯','🔮',
]

// ─── Layer display in canvas ───────────────────────────────────────────────────

function LayerContent({ layer }) {
    if (layer.type === 'text') {
        const d = layer.data
        return (
            <div className="ae-text" style={{
                fontSize:        `${d.fontSize}cqh`,
                fontWeight:      d.fontWeight,
                color:           d.color,
                textAlign:       d.align,
                backgroundColor: d.bgColor || 'transparent',
                padding:         d.bgColor ? `${d.bgPadding ?? 8}px` : 0,
                borderRadius:    d.bgColor ? `${d.bgRadius ?? 6}px`  : 0,
                maxWidth:        `${d.maxWidthPct ?? 90}cqw`,
                textShadow:      d.shadow ? '0 2px 8px rgba(0,0,0,.6)' : 'none',
            }}>
                {d.content || '\u00A0'}
            </div>
        )
    }
    if (layer.type === 'sticker') {
        const d = layer.data
        if (d.url) return <img className="ae-sticker" src={d.url} alt={d.alt || ''} />
        return <span className="ae-sticker ae-sticker--emoji">{d.emoji}</span>
    }
    if (layer.type === 'mention') {
        const d = layer.data
        return (
            <div className="ae-mention">
                <span className="ae-mention-at">@</span>{d.displayName || 'mention'}
            </div>
        )
    }
    if (layer.type === 'product-tag') {
        const d = layer.data
        return (
            <div className="ae-product-tag">
                {d.image && <img className="ae-product-tag-img" src={d.image} alt={d.name} />}
                <div className="ae-product-tag-info">
                    <span className="ae-product-tag-name">{d.name || 'Produit'}</span>
                    {d.price && <span className="ae-product-tag-price">{d.price} {d.currency || ''}</span>}
                </div>
            </div>
        )
    }
    return null
}

// ─── Canvas background ────────────────────────────────────────────────────────

function CanvasBg({ bg }) {
    if (!bg) return <div className="ae-canvas-bg" style={{ background: '#000' }} />
    if (bg.type === 'color')
        return <div className="ae-canvas-bg" style={{ background: bg.color || '#000' }} />
    if (bg.type === 'gradient') {
        const grad = `linear-gradient(${bg.angle ?? 135}deg, ${bg.from || '#000'}, ${bg.to || '#fff'})`
        return <div className="ae-canvas-bg" style={{ background: grad }} />
    }
    if (bg.type === 'media' && bg.previewUrl) {
        if (bg.mediaType === 'video')
            return <video className="ae-canvas-bg ae-canvas-bg--media" src={bg.previewUrl}
                style={{ objectFit: bg.fit || 'cover' }} autoPlay muted loop playsInline />
        return <img className="ae-canvas-bg ae-canvas-bg--media" src={bg.previewUrl} alt=""
            style={{ objectFit: bg.fit || 'cover' }} />
    }
    return <div className="ae-canvas-bg" style={{ background: '#1a1a2e' }} />
}

// ─── Page thumbnail ───────────────────────────────────────────────────────────

function PageThumb({ page }) {
    const bg = page.background
    let s = {}
    if (bg?.type === 'color') s = { background: bg.color }
    else if (bg?.type === 'gradient')
        s = { background: `linear-gradient(${bg.angle ?? 135}deg, ${bg.from || '#000'}, ${bg.to || '#fff'})` }
    else if (bg?.type === 'media' && bg.previewUrl)
        s = { backgroundImage: `url(${bg.previewUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    return (
        <div className="ae-thumb-inner" style={s}>
            {page.layers.slice(0, 3).map((_, i) => (
                <div key={i} className="ae-thumb-dot" />
            ))}
        </div>
    )
}

// ─── Layer list helpers ───────────────────────────────────────────────────────

function layerIcon(type) {
    if (type === 'text') return 'T'
    if (type === 'sticker') return '★'
    if (type === 'mention') return '@'
    if (type === 'product-tag') return '♦'
    return '?'
}

function layerLabel(layer) {
    if (layer.type === 'text') return layer.data.content?.slice(0, 22) || '(vide)'
    if (layer.type === 'sticker') return layer.data.emoji || 'Sticker'
    if (layer.type === 'mention') return '@' + (layer.data.displayName || 'mention')
    if (layer.type === 'product-tag') return layer.data.name || 'Produit'
    return '(calque)'
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ArticleEditor({ onBack, onDone, uploadBgFile, brandProducts = [] }) {
    const [pages, setPages]           = useState([mkPage()])
    const [pageIdx, setPageIdx]       = useState(0)
    const [selId, setSelId]           = useState(null)
    const [bgUploading, setBgUploading] = useState(false)
    const [bgProgress, setBgProgress]   = useState(0)
    const [bgError, setBgError]         = useState(null)
    const [copiedPage, setCopiedPage]   = useState(null)

    // Modals / pickers
    const [showStickerPicker, setShowStickerPicker] = useState(false)
    const [showMentionDialog, setShowMentionDialog] = useState(false)
    const [mentionName, setMentionName]             = useState('')
    const [mentionType, setMentionType]             = useState('user')
    const [showProductPicker, setShowProductPicker] = useState(false)

    const canvasRef = useRef(null)
    const drag      = useRef(null)   // { id, lx, ly }
    const bgFileRef = useRef(null)

    const page     = pages[pageIdx]
    const selLayer = page.layers.find(l => l.id === selId) ?? null
    const bg       = page.background

    // ─── Page mutations ───────────────────────────────────────────────────────

    const mutatePage = (fn) =>
        setPages(ps => ps.map((p, i) => i === pageIdx ? fn(p) : p))

    const setBg = (newBg) => mutatePage(p => ({ ...p, background: newBg }))

    const addPage = () => {
        setPages(ps => [...ps, mkPage()])
        setPageIdx(pages.length)
        setSelId(null)
    }

    const delPage = (i) => {
        if (pages.length === 1) return
        setPages(ps => ps.filter((_, j) => j !== i))
        setPageIdx(idx => Math.min(idx, pages.length - 2))
        setSelId(null)
    }

    const goPage = (i) => { setPageIdx(i); setSelId(null) }

    const copyPage = () => setCopiedPage(JSON.parse(JSON.stringify(page)))

    const pastePage = () => {
        if (!copiedPage) return
        const newPage = JSON.parse(JSON.stringify(copiedPage))
        newPage.id = uid()
        newPage.layers = newPage.layers.map(l => ({ ...l, id: uid() }))
        setPages(ps => [...ps.slice(0, pageIdx + 1), newPage, ...ps.slice(pageIdx + 1)])
        setPageIdx(pageIdx + 1)
        setSelId(null)
    }

    // ─── Layer mutations ──────────────────────────────────────────────────────

    const addLayer = (layer) => {
        mutatePage(p => ({ ...p, layers: [...p.layers, layer] }))
        setSelId(layer.id)
        setShowStickerPicker(false)
        setShowMentionDialog(false)
        setShowProductPicker(false)
    }

    const addText = () => addLayer(mkTextLayer())

    const pickSticker = (emoji) => addLayer(mkStickerLayer(emoji))

    const addMention = () => {
        if (!mentionName.trim()) return
        addLayer(mkMentionLayer(mentionName.trim(), mentionType))
        setMentionName('')
    }

    const addProductTag = (product) => addLayer(mkProductTagLayer(product))

    const delLayer = (id) => {
        mutatePage(p => ({ ...p, layers: p.layers.filter(l => l.id !== id) }))
        if (selId === id) setSelId(null)
    }

    const updLayer = (id, changes) =>
        mutatePage(p => ({
            ...p, layers: p.layers.map(l => l.id === id ? { ...l, ...changes } : l)
        }))

    const updLayerData = (id, changes) =>
        mutatePage(p => ({
            ...p, layers: p.layers.map(l => l.id === id ? { ...l, data: { ...l.data, ...changes } } : l)
        }))

    // ─── Drag ─────────────────────────────────────────────────────────────────

    const onPD = (e, layerId) => {
        e.preventDefault()
        e.stopPropagation()
        setSelId(layerId)
        drag.current = { id: layerId, lx: e.clientX, ly: e.clientY }
        e.currentTarget.setPointerCapture(e.pointerId)
    }

    const onPM = (e) => {
        if (!drag.current) return
        const canvas = canvasRef.current
        if (!canvas) return
        const rect = canvas.getBoundingClientRect()
        const dx = ((e.clientX - drag.current.lx) / rect.width)  * 100
        const dy = ((e.clientY - drag.current.ly) / rect.height) * 100
        drag.current.lx = e.clientX
        drag.current.ly = e.clientY
        const id = drag.current.id
        setPages(ps => ps.map((p, i) => {
            if (i !== pageIdx) return p
            return {
                ...p,
                layers: p.layers.map(l => l.id !== id ? l : {
                    ...l,
                    x: clamp(l.x + dx, 0, 100),
                    y: clamp(l.y + dy, 0, 100),
                }),
            }
        }))
    }

    const onPU = () => { drag.current = null }

    // ─── Background upload ────────────────────────────────────────────────────

    const handleBgFile = async (e) => {
        const file = e.target.files?.[0]
        if (!file) return
        e.target.value = ''
        const isVideo    = file.type.startsWith('video/')
        const previewUrl = URL.createObjectURL(file)
        const capturedIdx = pageIdx

        setPages(ps => ps.map((p, i) => i !== capturedIdx ? p : {
            ...p,
            background: { type: 'media', mediaType: isVideo ? 'video' : 'image', previewUrl, mediaId: null, fit: 'cover' },
        }))
        setBgUploading(true)
        setBgProgress(0)
        setBgError(null)

        try {
            const mediaId = await uploadBgFile(file, (pct) => setBgProgress(pct))
            setPages(ps => ps.map((p, i) => {
                if (i !== capturedIdx || p.background?.type !== 'media') return p
                return { ...p, background: { ...p.background, mediaId } }
            }))
        } catch (err) {
            setBgError(err.message)
            setPages(ps => ps.map((p, i) => {
                if (i !== capturedIdx || p.background?.type !== 'media') return p
                return { ...p, background: { type: 'color', color: '#1a1a2e' } }
            }))
        } finally {
            setBgUploading(false)
        }
    }

    // ─── Serialise & done ─────────────────────────────────────────────────────

    const handleDone = () => {
        const notReady = pages.some(p => p.background?.type === 'media' && !p.background.mediaId)
        if (notReady) { alert('Un upload de fond est encore en cours. Attendez la fin avant de continuer.'); return }

        const out = pages.map(p => {
            const b = p.background
            const bgOut = !b ? undefined
                : b.type === 'color'    ? { type: 'color', color: b.color }
                : b.type === 'gradient' ? { type: 'gradient', from: b.from, to: b.to, angle: b.angle }
                : { type: 'media', mediaId: b.mediaId, fit: b.fit }
            return {
                background: bgOut,
                layers: p.layers.map(l => ({
                    id: l.id, type: l.type,
                    x: l.x, y: l.y,
                    rotation: l.rotation, scale: l.scale, opacity: l.opacity,
                    data: l.data,
                })),
            }
        })
        onDone(out)
    }

    // ─── Background tab helpers ───────────────────────────────────────────────

    const selectBgColor = () => {
        if (bg?.type === 'color') return
        setBg({ type: 'color', color: bg?.color || '#1a1a2e' })
    }
    const selectBgGradient = () => {
        if (bg?.type === 'gradient') return
        setBg({ type: 'gradient', from: bg?.from || '#1a1a2e', to: bg?.to || '#e94560', angle: bg?.angle ?? 135 })
    }

    // ─── Render ───────────────────────────────────────────────────────────────

    const bgType = bg?.type || 'color'

    return (
        <div className="ae-root">

            {/* Header */}
            <div className="ae-header">
                <button className="ae-btn-outline" onClick={onBack}>← Retour</button>
                <span className="ae-header-title">
                    Éditeur d'article — page {pageIdx + 1}/{pages.length}
                </span>
                <button className="ae-btn-primary" onClick={handleDone}>Suivant →</button>
            </div>

            {/* Body */}
            <div className="ae-body">

                {/* Canvas column */}
                <div className="ae-canvas-col">
                    <div
                        ref={canvasRef}
                        className="ae-canvas"
                        onClick={() => setSelId(null)}
                    >
                        <CanvasBg bg={bg} />

                        {page.layers.map(layer => (
                            <div
                                key={layer.id}
                                className={`ae-layer${selId === layer.id ? ' ae-layer--sel' : ''}`}
                                style={{
                                    left:      `${layer.x}%`,
                                    top:       `${layer.y}%`,
                                    transform: `translate(-50%,-50%) rotate(${layer.rotation}deg) scale(${layer.scale})`,
                                    opacity:   layer.opacity,
                                }}
                                onPointerDown={e => onPD(e, layer.id)}
                                onPointerMove={onPM}
                                onPointerUp={onPU}
                                onPointerCancel={onPU}
                            >
                                <LayerContent layer={layer} />
                            </div>
                        ))}
                    </div>

                    {/* Page nav */}
                    <div className="ae-page-nav">
                        {pages.map((p, i) => (
                            <div
                                key={p.id}
                                className={`ae-page-thumb${i === pageIdx ? ' ae-page-thumb--active' : ''}`}
                                onClick={() => goPage(i)}
                            >
                                <PageThumb page={p} />
                                {pages.length > 1 && (
                                    <button
                                        className="ae-page-del"
                                        onClick={e => { e.stopPropagation(); delPage(i) }}
                                    >✕</button>
                                )}
                            </div>
                        ))}
                        {pages.length < 15 && (
                            <button className="ae-page-add" onClick={addPage}>+</button>
                        )}
                        <button className="ae-page-action" title="Copier cette page" onClick={copyPage}>⎘</button>
                        {copiedPage && (
                            <button className="ae-page-action ae-page-action--paste" title="Coller après" onClick={pastePage}>⎘+</button>
                        )}
                    </div>
                </div>

                {/* Panel */}
                <div className="ae-panel">

                    {/* Background */}
                    <div className="ae-panel-section">
                        <div className="ae-panel-title">Fond de page</div>
                        <div className="ae-panel-tabs">
                            <button
                                className={`ae-panel-tab${bgType === 'color' ? ' ae-panel-tab--on' : ''}`}
                                onClick={selectBgColor}
                            >Couleur</button>
                            <button
                                className={`ae-panel-tab${bgType === 'gradient' ? ' ae-panel-tab--on' : ''}`}
                                onClick={selectBgGradient}
                            >Dégradé</button>
                            <button
                                className={`ae-panel-tab${bgType === 'media' ? ' ae-panel-tab--on' : ''}`}
                                onClick={() => bgFileRef.current?.click()}
                            >Média</button>
                        </div>

                        {bgType === 'color' && (
                            <div className="ae-panel-row">
                                <input type="color" value={bg?.color || '#1a1a2e'}
                                    onChange={e => setBg({ type: 'color', color: e.target.value })}
                                    className="ae-color-big" />
                                <span className="ae-panel-muted">{bg?.color || '#1a1a2e'}</span>
                            </div>
                        )}

                        {bgType === 'gradient' && (
                            <>
                                <div className="ae-panel-row">
                                    <span className="ae-panel-muted ae-min">De</span>
                                    <input type="color" value={bg?.from || '#1a1a2e'}
                                        onChange={e => setBg({ ...bg, from: e.target.value })}
                                        className="ae-color-big" />
                                </div>
                                <div className="ae-panel-row">
                                    <span className="ae-panel-muted ae-min">À</span>
                                    <input type="color" value={bg?.to || '#e94560'}
                                        onChange={e => setBg({ ...bg, to: e.target.value })}
                                        className="ae-color-big" />
                                </div>
                                <div className="ae-panel-col">
                                    <span className="ae-panel-label">Angle ({bg?.angle ?? 135}°)</span>
                                    <input type="range" min={0} max={360} value={bg?.angle ?? 135}
                                        onChange={e => setBg({ ...bg, angle: +e.target.value })}
                                        className="ae-range" />
                                </div>
                            </>
                        )}

                        {bgType === 'media' && (
                            <>
                                {bgUploading ? (
                                    <div className="ae-panel-row">
                                        <div className="ae-progress-bar">
                                            <div className="ae-progress-fill" style={{ width: `${bgProgress}%` }} />
                                        </div>
                                        <span className="ae-panel-muted">{bgProgress}%</span>
                                    </div>
                                ) : (
                                    <button className="ae-btn-sm ae-btn-outline-dark"
                                        onClick={() => bgFileRef.current?.click()}>
                                        Changer le fond
                                    </button>
                                )}
                                {bgError && <p className="ae-error">{bgError}</p>}
                                <div className="ae-panel-col">
                                    <span className="ae-panel-label">Ajustement</span>
                                    <div className="ae-panel-btns">
                                        {[['cover','Remplir'], ['contain','Contenir']].map(([f, label]) => (
                                            <button key={f}
                                                className={`ae-panel-btn${bg?.fit === f ? ' ae-panel-btn--on' : ''}`}
                                                onClick={() => setBg({ ...bg, fit: f })}
                                            >{label}</button>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}

                        <input ref={bgFileRef} type="file" style={{ display: 'none' }}
                            accept="image/jpeg,image/png,image/webp,image/avif,video/mp4,video/quicktime,video/webm"
                            onChange={handleBgFile} />
                    </div>

                    {/* Layers — add buttons */}
                    <div className="ae-panel-section">
                        <div className="ae-panel-title">Calques</div>
                        <div className="ae-panel-btns">
                            <button className="ae-panel-btn" onClick={addText}>T Texte</button>
                            <button
                                className={`ae-panel-btn${showStickerPicker ? ' ae-panel-btn--on' : ''}`}
                                onClick={() => { setShowStickerPicker(s => !s); setShowMentionDialog(false); setShowProductPicker(false) }}
                            >★ Sticker</button>
                        </div>
                        <div className="ae-panel-btns">
                            <button
                                className={`ae-panel-btn${showMentionDialog ? ' ae-panel-btn--on' : ''}`}
                                onClick={() => { setShowMentionDialog(s => !s); setShowStickerPicker(false); setShowProductPicker(false) }}
                            >@ Mention</button>
                            {brandProducts.length > 0 && (
                                <button
                                    className={`ae-panel-btn${showProductPicker ? ' ae-panel-btn--on' : ''}`}
                                    onClick={() => { setShowProductPicker(s => !s); setShowStickerPicker(false); setShowMentionDialog(false) }}
                                >♦ Produit</button>
                            )}
                        </div>

                        {/* Sticker picker */}
                        {showStickerPicker && (
                            <div className="ae-sticker-grid">
                                {STICKER_EMOJIS.map(emoji => (
                                    <button key={emoji} className="ae-sticker-btn" onClick={() => pickSticker(emoji)}>
                                        {emoji}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Mention dialog */}
                        {showMentionDialog && (
                            <div className="ae-mention-dialog">
                                <div className="ae-panel-col">
                                    <span className="ae-panel-label">Nom affiché</span>
                                    <input
                                        className="ae-input"
                                        placeholder="ex: wink_officiel"
                                        value={mentionName}
                                        onChange={e => setMentionName(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && addMention()}
                                    />
                                </div>
                                <div className="ae-panel-col">
                                    <span className="ae-panel-label">Type</span>
                                    <div className="ae-panel-btns">
                                        {[['user','Utilisateur'],['brand','Marque']].map(([t, lbl]) => (
                                            <button key={t}
                                                className={`ae-panel-btn${mentionType === t ? ' ae-panel-btn--on' : ''}`}
                                                onClick={() => setMentionType(t)}
                                            >{lbl}</button>
                                        ))}
                                    </div>
                                </div>
                                <button className="ae-btn-full ae-btn-outline-dark" onClick={addMention}>
                                    + Ajouter
                                </button>
                            </div>
                        )}

                        {/* Product picker */}
                        {showProductPicker && (
                            <div className="ae-product-list">
                                {brandProducts.map(p => (
                                    <button key={p.id} className="ae-product-item" onClick={() => addProductTag(p)}>
                                        {p.image && <img className="ae-product-item-img" src={p.image} alt={p.name} />}
                                        <div className="ae-product-item-info">
                                            <span className="ae-product-item-name">{p.name}</span>
                                            {p.price != null && (
                                                <span className="ae-product-item-price">{p.price} {p.currency || ''}</span>
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}

                        {page.layers.length === 0 && (
                            <p className="ae-hint">Aucun calque sur cette page.</p>
                        )}
                        {page.layers.map(l => (
                            <div
                                key={l.id}
                                className={`ae-layer-item${selId === l.id ? ' ae-layer-item--on' : ''}`}
                                onClick={() => setSelId(l.id)}
                            >
                                <span className="ae-layer-item-icon">{layerIcon(l.type)}</span>
                                <span className="ae-layer-item-txt">{layerLabel(l)}</span>
                                <button className="ae-btn-icon-danger"
                                    onClick={e => { e.stopPropagation(); delLayer(l.id) }}>✕</button>
                            </div>
                        ))}
                    </div>

                    {/* Properties — text */}
                    {selLayer?.type === 'text' && (
                        <div className="ae-panel-section">
                            <div className="ae-panel-title">Propriétés du texte</div>

                            <div className="ae-panel-col">
                                <span className="ae-panel-label">Contenu</span>
                                <textarea className="ae-textarea" rows={3}
                                    value={selLayer.data.content}
                                    onChange={e => updLayerData(selId, { content: e.target.value })} />
                            </div>

                            <div className="ae-panel-col">
                                <span className="ae-panel-label">Taille ({selLayer.data.fontSize} cqh)</span>
                                <input type="range" min={2} max={15} step={0.5}
                                    value={selLayer.data.fontSize}
                                    onChange={e => updLayerData(selId, { fontSize: +e.target.value })}
                                    className="ae-range" />
                            </div>

                            <div className="ae-panel-col">
                                <span className="ae-panel-label">Graisse</span>
                                <div className="ae-panel-btns">
                                    {[['400','Normal'],['700','Gras'],['900','Noir']].map(([w, lbl]) => (
                                        <button key={w}
                                            className={`ae-panel-btn${selLayer.data.fontWeight === w ? ' ae-panel-btn--on' : ''}`}
                                            style={{ fontWeight: w }}
                                            onClick={() => updLayerData(selId, { fontWeight: w })}
                                        >{lbl}</button>
                                    ))}
                                </div>
                            </div>

                            <div className="ae-panel-row">
                                <span className="ae-panel-label">Couleur</span>
                                <input type="color" value={selLayer.data.color}
                                    onChange={e => updLayerData(selId, { color: e.target.value })}
                                    className="ae-color-big" />
                            </div>

                            <div className="ae-panel-col">
                                <span className="ae-panel-label">Alignement</span>
                                <div className="ae-panel-btns">
                                    {[['left','G'],['center','C'],['right','D']].map(([a, lbl]) => (
                                        <button key={a}
                                            className={`ae-panel-btn${selLayer.data.align === a ? ' ae-panel-btn--on' : ''}`}
                                            onClick={() => updLayerData(selId, { align: a })}
                                        >{lbl}</button>
                                    ))}
                                </div>
                            </div>

                            <div className="ae-panel-check">
                                <label>
                                    <input type="checkbox"
                                        checked={!!selLayer.data.bgColor}
                                        onChange={e => updLayerData(selId, { bgColor: e.target.checked ? '#000000' : null })} />
                                    {' '}Fond coloré
                                </label>
                                {selLayer.data.bgColor && (
                                    <input type="color" value={selLayer.data.bgColor}
                                        onChange={e => updLayerData(selId, { bgColor: e.target.value })}
                                        className="ae-color-sm" />
                                )}
                            </div>

                            <div className="ae-panel-check">
                                <label>
                                    <input type="checkbox"
                                        checked={!!selLayer.data.shadow}
                                        onChange={e => updLayerData(selId, { shadow: e.target.checked })} />
                                    {' '}Ombre portée
                                </label>
                            </div>

                            <div className="ae-panel-col">
                                <span className="ae-panel-label">Opacité ({Math.round(selLayer.opacity * 100)}%)</span>
                                <input type="range" min={0} max={1} step={0.05}
                                    value={selLayer.opacity}
                                    onChange={e => updLayer(selId, { opacity: +e.target.value })}
                                    className="ae-range" />
                            </div>

                            <div className="ae-panel-col">
                                <span className="ae-panel-label">Rotation ({selLayer.rotation}°)</span>
                                <input type="range" min={-180} max={180} step={1}
                                    value={selLayer.rotation}
                                    onChange={e => updLayer(selId, { rotation: +e.target.value })}
                                    className="ae-range" />
                            </div>
                        </div>
                    )}

                    {/* Properties — sticker */}
                    {selLayer?.type === 'sticker' && (
                        <div className="ae-panel-section">
                            <div className="ae-panel-title">Propriétés du sticker</div>
                            <div className="ae-sticker-preview">{selLayer.data.emoji || '🖼'}</div>
                            <div className="ae-panel-col">
                                <span className="ae-panel-label">Taille ({selLayer.scale.toFixed(1)}×)</span>
                                <input type="range" min={0.2} max={5} step={0.1}
                                    value={selLayer.scale}
                                    onChange={e => updLayer(selId, { scale: +e.target.value })}
                                    className="ae-range" />
                            </div>
                            <div className="ae-panel-col">
                                <span className="ae-panel-label">Opacité ({Math.round(selLayer.opacity * 100)}%)</span>
                                <input type="range" min={0} max={1} step={0.05}
                                    value={selLayer.opacity}
                                    onChange={e => updLayer(selId, { opacity: +e.target.value })}
                                    className="ae-range" />
                            </div>
                            <div className="ae-panel-col">
                                <span className="ae-panel-label">Rotation ({selLayer.rotation}°)</span>
                                <input type="range" min={-180} max={180} step={1}
                                    value={selLayer.rotation}
                                    onChange={e => updLayer(selId, { rotation: +e.target.value })}
                                    className="ae-range" />
                            </div>
                        </div>
                    )}

                    {/* Properties — mention */}
                    {selLayer?.type === 'mention' && (
                        <div className="ae-panel-section">
                            <div className="ae-panel-title">Propriétés de la mention</div>
                            <div className="ae-panel-col">
                                <span className="ae-panel-label">Nom affiché</span>
                                <input className="ae-input"
                                    value={selLayer.data.displayName}
                                    onChange={e => updLayerData(selId, { displayName: e.target.value })} />
                            </div>
                            <div className="ae-panel-col">
                                <span className="ae-panel-label">Type</span>
                                <div className="ae-panel-btns">
                                    {[['user','Utilisateur'],['brand','Marque']].map(([t, lbl]) => (
                                        <button key={t}
                                            className={`ae-panel-btn${selLayer.data.entityType === t ? ' ae-panel-btn--on' : ''}`}
                                            onClick={() => updLayerData(selId, { entityType: t })}
                                        >{lbl}</button>
                                    ))}
                                </div>
                            </div>
                            <div className="ae-panel-col">
                                <span className="ae-panel-label">Opacité ({Math.round(selLayer.opacity * 100)}%)</span>
                                <input type="range" min={0} max={1} step={0.05}
                                    value={selLayer.opacity}
                                    onChange={e => updLayer(selId, { opacity: +e.target.value })}
                                    className="ae-range" />
                            </div>
                            <div className="ae-panel-col">
                                <span className="ae-panel-label">Rotation ({selLayer.rotation}°)</span>
                                <input type="range" min={-180} max={180} step={1}
                                    value={selLayer.rotation}
                                    onChange={e => updLayer(selId, { rotation: +e.target.value })}
                                    className="ae-range" />
                            </div>
                        </div>
                    )}

                    {/* Properties — product-tag */}
                    {selLayer?.type === 'product-tag' && (
                        <div className="ae-panel-section">
                            <div className="ae-panel-title">Tag produit</div>
                            <p className="ae-hint" style={{ textAlign: 'left', color: 'rgba(255,255,255,.7)' }}>
                                {selLayer.data.name}
                            </p>
                            {selLayer.data.price && (
                                <p className="ae-hint" style={{ textAlign: 'left' }}>
                                    {selLayer.data.price} {selLayer.data.currency || ''}
                                </p>
                            )}
                            <div className="ae-panel-col">
                                <span className="ae-panel-label">Opacité ({Math.round(selLayer.opacity * 100)}%)</span>
                                <input type="range" min={0} max={1} step={0.05}
                                    value={selLayer.opacity}
                                    onChange={e => updLayer(selId, { opacity: +e.target.value })}
                                    className="ae-range" />
                            </div>
                            <div className="ae-panel-col">
                                <span className="ae-panel-label">Rotation ({selLayer.rotation}°)</span>
                                <input type="range" min={-180} max={180} step={1}
                                    value={selLayer.rotation}
                                    onChange={e => updLayer(selId, { rotation: +e.target.value })}
                                    className="ae-range" />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
